import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import { rem } from 'polished'
import debounce from 'lodash/debounce'
import { getAirportDisplayText } from 'lib/flights'
import { getAirportsSearch } from 'api/flights'
import { StandaloneFlightMenu } from 'components/Flights/FlightsSearch/SearchWidget/SearchWidget'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { connect } from 'react-redux'
import { PopularAirports } from 'components/Flights/types'
import { filterAirportRecords } from 'lib/flights'

import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Input from 'components/Common/Form/Input/Input'
import AirportSelectMenuDesktop from './AirportSelectMenuDesktop'
import AirportSelectMenuMobile from './AirportSelectMenuMobile'
import Pane from 'components/Common/Pane'
import Breakpoint from 'components/Common/Breakpoint'
import FlightSearchMenu from '../FlightSearchMenu/FlightSearchMenu'
import Caption from 'components/Luxkit/Typography/Caption'

const StyledPane = styled(Pane)`
  display: flex;
  flex: 1;
`

const AirportToggle = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.five};
  padding: 0 ${rem(16)};
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${rem(48)};
  flex: 1;

  ${mediaQueryUp.lg} {
    width: ${rem(220)};
  }

  &.error {
    background: ${props => props.theme.palette.messaging.critical.light};
    border: 1px solid ${props => props.theme.palette.messaging.critical.main};
  }
`

const InputWrapper = styled.div`
  width: 100%;
  position: relative;
`

const StyledInput = styled(Input)`
  border: none;
  padding: 0;
  position: absolute;
  top: 0;
  height: 100%;
  font-size: ${rem(14)};
  font-weight: ${props => props.theme.font.primary.weight.semiBold};
  background: transparent;
  color: transparent;
  transition: background .2s, color .2s;
  width: 100%;

  &:focus {
    background: ${props => props.theme.palette.neutral.eight};
    color: ${props => props.theme.palette.neutral.one};

    &.error {
      background: ${props => props.theme.palette.messaging.critical.light};
    }
  }
`

interface Props {
  title: string;
  isDeparture: boolean;
  className?: string;
  airport: App.AirportLocation;
  activeMenu: StandaloneFlightMenu;
  showDepartureError?: boolean;
  showDesinationError?: boolean;
  popularAirports: PopularAirports;
  setActiveMenu: (menu: StandaloneFlightMenu) => void;
  useOnlyMobile?: boolean;
}

function LocationInput(props: Props) {
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const {
    title,
    isDeparture,
    className,
    airport,
    activeMenu,
    popularAirports,
    setActiveMenu,
    useOnlyMobile,
  } = props

  const triggerRef = useRef()
  const { showDepartureError, showDesinationError, showSameAirportError, departureAirport, arrivalAirport } = state
  const [inputValue, setInputValue] = useState(airport ? getAirportDisplayText(airport) : '')
  const [airportLocations, setAirportLocations] = useState<Array<App.AirportLocation>>([])
  const [locationsFetching, setLocationsFetching] = useState(false)
  const currentMenu = isDeparture ? StandaloneFlightMenu.departAirport : StandaloneFlightMenu.arrivalAirport
  const actionType = isDeparture ? FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT : FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT
  const onClose = useCallback(() => setActiveMenu(null), [setActiveMenu])
  const heading = inputValue ? null : 'Popular airports'
  const popularAirportsList = isDeparture ? popularAirports.origins : popularAirports.destinations

  const filteredAirports = useMemo(() => filterAirportRecords(
    airportLocations,
    isDeparture ? arrivalAirport : departureAirport,
  ), [airportLocations, isDeparture, departureAirport, arrivalAirport])

  const debouncedLoadAirports = useMemo(() => debounce((value: string) => {
    getAirportsSearch(value).then(airports => {
      setLocationsFetching(false)
      setAirportLocations(airports)
    })
  }, 500), [setAirportLocations])

  const onChangeInputText = useCallback(e => {
    const { value } = e.target
    setInputValue(value)
    const searchText = value.trim()

    if (searchText.length > 2) {
      setLocationsFetching(true)
      debouncedLoadAirports(searchText)
    }
    if (activeMenu !== currentMenu) {
      setActiveMenu(currentMenu)
    }

    if (value.length === 0) {
      dispatch({ type: actionType, airport: null })
    }
  }, [activeMenu, currentMenu, debouncedLoadAirports, setActiveMenu, dispatch, setInputValue, actionType])

  const onSelectAirport = useCallback(airport => {
    setActiveMenu(isDeparture ? null : StandaloneFlightMenu.date)
    setInputValue(getAirportDisplayText(airport))
    dispatch({ type: actionType, airport })
    setAirportLocations(popularAirportsList)
  }, [isDeparture, actionType, popularAirportsList, setActiveMenu, setInputValue, dispatch, setAirportLocations])

  useEffect(() => {
    setInputValue(airport ? getAirportDisplayText(airport) : '')
  }, [airport])

  useEffect(() => {
    if (!inputValue.length && popularAirportsList?.length > 0) {
      setAirportLocations(popularAirportsList)
    }
  }, [inputValue, popularAirportsList])

  const onInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value) {
      e.target.select()
    } else {
      setActiveMenu(currentMenu)
    }
  }, [currentMenu, setActiveMenu])

  const showLocationError = (isDeparture && showDepartureError) || (!isDeparture && showDesinationError) || showSameAirportError

  const FloatingMenu = ({ children }) => (
    <FlightSearchMenu
      open={activeMenu === currentMenu}
      title={isDeparture ? 'From' : 'To'}
      onApply={onClose}
      onCancel={onClose}
    >
      {children}
    </FlightSearchMenu>
  )

  return (
    <StyledPane type="clear">
      <AirportToggle className={cn(className, { error: showLocationError })} ref={triggerRef}>
        <InputWrapper>
          <Caption variant="large" weight="bold">{title}</Caption>
          <BodyText colour={inputValue === '' ? 'neutral-two' : undefined} variant="medium" wrap="truncate">
            {inputValue || 'Search by city or airport'}
          </BodyText>
          <StyledInput
            className={cn({ error: showLocationError })}
            data-testid={currentMenu}
            value={inputValue}
            onChange={onChangeInputText}
            onFocus={onInputFocus}
          />
        </InputWrapper>
      </AirportToggle>
      {
        !useOnlyMobile && (
          <Breakpoint min="sm">
            {activeMenu === currentMenu &&
              <AirportSelectMenuDesktop
                open={activeMenu === currentMenu}
                loading={locationsFetching}
                onClose={onClose}
                onSelectAirport={onSelectAirport}
                onChangeText={onChangeInputText}
                airports={filteredAirports}
                inputValue={inputValue}
                triggerRef={triggerRef}
                heading={heading}
              />
            }
          </Breakpoint>
        )
      }
      <Breakpoint
        max={useOnlyMobile ? undefined : 'xs'}
      >
        <AirportSelectMenuMobile
          open={activeMenu === currentMenu}
          loading={locationsFetching}
          onSelectAirport={onSelectAirport}
          onChangeText={onChangeInputText}
          inputValue={inputValue}
          airports={filteredAirports}
          heading={heading}
          wrapper={FloatingMenu}
        />
      </Breakpoint>
    </StyledPane>
  )
}

function mapStateToProps(state: App.State) {
  return {
    popularAirports: state.flights.popularAirports,
  }
}

export default connect(mapStateToProps)(LocationInput)
